import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

// ── Metric display config ────────────────────────────────────────────────────

export const METRIC_CONFIG = {
  total_return:      { label: 'Total Return',       format: 'percent' },
  annualized_return: { label: 'Annualized Return',  format: 'percent' },
  volatility:        { label: 'Volatility',          format: 'percent' },
  sharpe_ratio:      { label: 'Sharpe Ratio',        format: 'decimal' },
  sortino_ratio:     { label: 'Sortino Ratio',       format: 'decimal' },
  calmar_ratio:      { label: 'Calmar Ratio',        format: 'decimal' },
  max_drawdown_pct:  { label: 'Max Drawdown',        format: 'percent' },
  beta:              { label: 'Beta',                 format: 'decimal' },
  alpha:             { label: 'Alpha',                format: 'percent' },
  r_squared:         { label: 'R-Squared',            format: 'decimal' },
  correlation:       { label: 'Correlation',          format: 'decimal' },
  tracking_error:    { label: 'Tracking Error',       format: 'percent' },
  info_ratio:        { label: 'Information Ratio',    format: 'decimal' },
  return_pct:        { label: 'Return %',             format: 'percent' },
  win_rate:          { label: 'Win Rate',             format: 'percent' },
  profit_factor:     { label: 'Profit Factor',        format: 'decimal' },
  avg_r_multiple:    { label: 'Avg R-Multiple',       format: 'decimal' },
  recovery_factor:   { label: 'Recovery Factor',      format: 'decimal' },
  consistency_score: { label: 'Consistency Score',     format: 'integer' },
}

// Metrics that can be negative — show a zero reference line
const CAN_BE_NEGATIVE = new Set([
  'total_return', 'annualized_return', 'alpha', 'return_pct',
  'sharpe_ratio', 'sortino_ratio', 'calmar_ratio', 'info_ratio',
  'avg_r_multiple', 'correlation',
])

// ── Format helpers ───────────────────────────────────────────────────────────

function formatValue(val, format) {
  if (val == null) return '—'
  const n = Number(val)
  switch (format) {
    case 'percent':
      return (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    case 'decimal':
      return (n >= 0 ? '+' : '') + n.toFixed(2)
    case 'integer':
      return Math.round(n).toString()
    default:
      return n.toFixed(2)
  }
}

function formatYAxis(val, format) {
  switch (format) {
    case 'percent':
      return val.toFixed(1) + '%'
    case 'integer':
      return Math.round(val).toString()
    default:
      return val.toFixed(2)
  }
}

// ── Tooltip ──────────────────────────────────────────────────────────────────

function TrendTooltip({ active, payload, format }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-[10px] text-inkMuted mb-1">{d.dateLabel}</div>
      <div className="font-mono text-xs font-semibold" style={{ color: 'rgb(var(--color-forest))' }}>
        {formatValue(d.value, format)}
      </div>
    </div>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function MetricTrendSlideOver({
  metricName,
  metricView,
  benchmark = null,
  currentValue,
  onClose,
}) {
  const { user } = useAuth()
  const panelRef = useRef(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const config = METRIC_CONFIG[metricName] || { label: metricName, format: 'decimal' }

  // Close on Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // Fetch history
  useEffect(() => {
    if (!user) return
    let cancelled = false

    async function fetchHistory() {
      if (!data) setLoading(true)
      let query = supabase
        .from('metrics_history')
        .select('metric_date, metric_value')
        .eq('user_id', user.id)
        .eq('metric_name', metricName)
        .eq('metric_view', metricView)
        .order('metric_date', { ascending: true })

      if (benchmark) {
        query = query.eq('benchmark', benchmark)
      } else {
        query = query.or('benchmark.is.null,benchmark.eq.')
      }

      const { data: rows } = await query
      if (cancelled) return

      setData(rows || [])
      setLoading(false)
    }

    fetchHistory()
    return () => { cancelled = true }
  }, [user, metricName, metricView, benchmark])

  // Format chart data
  const chartData = (data || []).map(row => {
    const d = new Date(row.metric_date + 'T00:00:00')
    return {
      date: row.metric_date,
      dateLabel: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      value: Number(row.metric_value),
    }
  })

  const showZeroLine = CAN_BE_NEGATIVE.has(metricName)
  const hasData = chartData.length >= 2

  // Determine X axis tick formatting
  const rangeDays = chartData.length > 1
    ? (new Date(chartData[chartData.length - 1].date) - new Date(chartData[0].date)) / 86400000
    : 0
  const longRange = rangeDays > 180

  const xTickData = chartData.map(d => {
    const dt = new Date(d.date + 'T00:00:00')
    return longRange
      ? dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  })

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{ background: 'rgba(0, 0, 0, 0.4)' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="relative w-full sm:w-[50vw] sm:min-w-[420px] sm:max-w-[700px] h-full flex flex-col animate-slide-in-right"
        style={{
          background: 'rgb(var(--color-canvas))',
          borderLeft: '1px solid rgb(var(--color-border))',
        }}
      >
        {/* Close button */}
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors hover:bg-canvas2"
            title="Close"
          >
            <X size={14} className="text-inkMuted" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">
              {metricView === 'portfolio' ? 'Portfolio' : 'Trading'} Metric History
            </div>
            <h2 className="font-black text-xl uppercase tracking-wide text-ink">
              {config.label}
            </h2>
            {currentValue != null && (
              <div className="font-mono text-sm text-inkMuted mt-1">
                Current: <span className="font-semibold text-ink">{formatValue(currentValue, config.format)}</span>
              </div>
            )}
            {benchmark && (
              <div className="font-mono text-[10px] text-inkMuted mt-0.5">vs {benchmark}</div>
            )}
          </div>

          {/* Chart */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="font-mono text-xs text-inkMuted animate-pulse">Loading history…</div>
            </div>
          ) : !hasData ? (
            <div className="bg-canvas2 border border-border rounded-xl px-6 py-16 text-center">
              <div className="font-mono text-xs text-inkMuted leading-relaxed">
                Not enough history yet.<br />
                Check back after a few days.
              </div>
            </div>
          ) : (
            <div className="card">
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={chartData.map((d, i) => ({ ...d, xLabel: xTickData[i] }))}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <XAxis
                    dataKey="xLabel"
                    tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                    axisLine={{ stroke: 'rgb(var(--color-border))' }}
                    tickLine={false}
                    minTickGap={60}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={v => formatYAxis(v, config.format)}
                    width={55}
                  />
                  <Tooltip content={<TrendTooltip format={config.format} />} />
                  {showZeroLine && (
                    <ReferenceLine
                      y={0}
                      stroke="rgb(var(--color-border))"
                      strokeDasharray="4 2"
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="rgb(var(--color-forest))"
                    strokeWidth={2}
                    dot={chartData.length <= 30}
                    activeDot={{ r: 4, fill: 'rgb(var(--color-forest))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 300ms ease both;
        }
      `}</style>
    </div>
  )
}
